import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Portal from 'app/components/shared/Portal';
import { bindAll } from '@bibliocommons/utils-react';
import { getHistoryState } from '@bibliocommons/utils-browser';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { childrenShape } from '@bibliocommons/bc-prop-types';
import { Close } from '@bibliocommons/base-icons';
import { IconButton } from '@bibliocommons/base-buttons';
import Context from '@bibliocommons/context';
import { useLocation } from 'react-router';

import './FullScreenOverlay.scss';

const ESCAPE_KEY_NAME = 'Escape';
const ESCAPE_KEY_CODE = 27;

/**
 * Supplies the current location to a class component. Replaces `withRouter` from Glue's
 * utils-routing, which moved to Core UI under CORE-79046: this component never read anything from
 * the router but the location, and used it only as a change signal (see componentDidUpdate), so
 * the whole router object was more than it needed.
 */
const withLocation = Component => {
  const Wrapper = props => <Component {...props} location={useLocation()} />;
  Wrapper.displayName = `withLocation(${Component.displayName || Component.name || 'Component'})`;
  return Wrapper;
};

export class FullScreenOverlay extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.containerRef = null;
    this.fullScreenOverlayId = uniqueId('full-screen-overlay-');
  }

  componentDidMount() {
    const { open } = this.props;
    if (open) {
      this.handleToggleOpen(open);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open !== prevProps.open) {
      this.handleToggleOpen(this.props.open);
    }
    if (this.props.open && this.props.location !== prevProps.location) {
      this.handlePushState();
    }
  }

  componentWillUnmount() {
    this.handleToggleOpen(false);
  }

  handlePushState() {
    if (this.props.disableClose) {
      return;
    }

    const historyState = getHistoryState();
    if (historyState.fullScreenOverlayId === this.fullScreenOverlayId) {
      return;
    }

    window.history.pushState({ ...historyState, fullScreenOverlayId: this.fullScreenOverlayId }, '');
  }

  handleClose() {
    if (!this.props.disableClose) {
      window.history.back();
    }
  }

  handleToggleOpen(open) {
    // Remove the event listener on close, and add on open
    const eventListener = open ? 'addEventListener' : 'removeEventListener';
    document[eventListener]('keydown', this.handleCloseOnEscape, true);

    const toggle = open ? 'add' : 'remove';
    document.documentElement.classList[toggle]('full-screen-overlay--open');

    if (!this.props.disableClose) {
      window[eventListener]('popstate', this.handlePopState);
    }

    if (open) {
      this.handlePushState();

      if (this.containerRef) {
        this.containerRef.scrollTop = 0;
      }
    }
  }

  handleCloseOnEscape(e) {
    if (e.key === ESCAPE_KEY_NAME || e.which === ESCAPE_KEY_CODE) {
      this.handleClose();
    }
  }

  handlePopState() {
    if (this.props.disableClose) {
      return;
    }

    const historyState = getHistoryState();

    if (this.props.open && historyState.fullScreenOverlayId !== this.fullScreenOverlayId) {
      this.props.handleClose();
    }
  }

  renderCloseButton() {
    return (
      !this.props.disableClose && (
        <IconButton dataKey="overlay-close" className="full-screen-overlay__close" handleClick={this.handleClose}>
          <Close />
          <ScreenReaderMessage>
            <FormattedMessage id="close_overlay" />
          </ScreenReaderMessage>
        </IconButton>
      )
    );
  }

  render() {
    const { children, className, dataKey, open = false } = this.props;
    const classNames = cn('cp-full-screen-overlay', className, {
      'full-screen-overlay--mobile-app': this.context.mobileApp
    });

    return (
      <Portal focusTrapActive={open}>
        <CSSTransition
          in={open}
          classNames="full-screen-overlay-fade"
          timeout={{ enter: 250, exit: 250 }}
          mountOnEnter
          unmountOnExit
        >
          <div
            className={classNames}
            role="dialog"
            aria-hidden={!open}
            data-key={dataKey}
            ref={el => {
              this.containerRef = el;
            }}
          >
            {!this.context.mobileApp && this.renderCloseButton()}
            {children}
          </div>
        </CSSTransition>
      </Portal>
    );
  }
}

FullScreenOverlay.propTypes = {
  className: PropTypes.string,
  children: childrenShape,
  dataKey: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func,
  disableClose: PropTypes.bool,
  location: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
};

FullScreenOverlay.defaultProps = {
  handleClose() {}
};

FullScreenOverlay.contextType = Context;

export default withLocation(FullScreenOverlay);
